import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import type { ArInvoiceListRow } from '@dcc-erp/shared';
import { fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { RowActions } from './RowActions';

// Router-free invoice table. The route owns data fetching, filtering, selection
// state, and navigation; this component owns presentation + the TanStack row
// model (sort + pagination). Cell markup is preserved verbatim from the
// pre-extraction inline <table> in ar.tsx so UI fidelity holds.

export interface ArInvoiceTableProps {
  rows: ArInvoiceListRow[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (ids: string[], checked: boolean) => void;
  onOpen: (id: string) => void; // voucherNo click → AR-02 navigation
  onReceive: (row: ArInvoiceListRow) => void;
  onVoid?: (row: ArInvoiceListRow) => void; // ยกเลิก → detail page (hosts the void modal)
  nowMs: number;
}

// Row criticality drives the due-date / aging cell tone classes. Only open
// (POSTED) invoices carry a signal; settled / void rows stay neutral.
function criticality(row: ArInvoiceListRow, nowMs: number): 'ok' | 'soon' | 'over' {
  if (row.status !== 'POSTED') return 'ok';
  const due = new Date(row.dueDate).getTime();
  if (due < nowMs) return 'over';
  if (due < nowMs + 7 * 864e5) return 'soon';
  return 'ok';
}

// Existing prototype.css tone classes: .neg (red) / .warn (amber).
const TONE_CLASS: Record<'ok' | 'soon' | 'over', string> = {
  ok: '',
  soon: 'warn',
  over: 'neg',
};

const PAGE_SIZE = 25;

export function ArInvoiceTable({
  rows,
  selected,
  onToggle,
  onToggleAll,
  onOpen,
  onReceive,
  onVoid,
  nowMs,
}: ArInvoiceTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);

  // Columns exist only to drive the row model (sort keys + pagination). The
  // visible cells are rendered with bespoke JSX below to keep exact markup, so
  // the column ids here just have to match the sortable headers.
  const columns = useMemo<ColumnDef<ArInvoiceListRow>[]>(
    () => [
      {
        id: 'outstanding',
        accessorFn: (r) =>
          toNumber(r.totalAmount) - toNumber(r.paidAmount) - toNumber(r.creditedAmount),
        sortDescFirst: false,
      },
      {
        id: 'agingDays',
        accessorFn: (r) => r.agingDays,
        sortDescFirst: false,
      },
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const outstandingCol = table.getColumn('outstanding');
  const agingCol = table.getColumn('agingDays');

  const pageRows = table.getRowModel().rows;
  // Select-all operates over the FULL filtered set (every page), not just the
  // visible page — so the checkbox + its checked state reflect all `rows`.
  const allIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allChecked = allIds.length > 0 && allIds.every((id) => selected.has(id));

  const { pageIndex, pageSize } = table.getState().pagination;
  const totalRows = rows.length;
  const firstShown = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const lastShown = Math.min((pageIndex + 1) * pageSize, totalRows);

  function sortArrow(col: typeof outstandingCol) {
    const dir = col?.getIsSorted();
    if (dir === 'asc') return ' ▲';
    if (dir === 'desc') return ' ▼';
    return '';
  }

  return (
    <>
      <div className="tbl-wrap" style={{ maxHeight: 540 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => onToggleAll(allIds, e.target.checked)}
                />
              </th>
              <th>เลขที่ใบแจ้งหนี้</th>
              <th>ลูกค้า</th>
              <th>สาขา</th>
              <th>วันที่ออก</th>
              <th>กำหนดชำระ</th>
              <th className="num">มูลค่ารวม</th>
              <th className="num">รับชำระ</th>
              <th className="num">
                <button
                  type="button"
                  className="th-sort"
                  onClick={outstandingCol?.getToggleSortingHandler()}
                >
                  คงค้าง{sortArrow(outstandingCol)}
                </button>
              </th>
              <th className="num">
                <button
                  type="button"
                  className="th-sort"
                  onClick={agingCol?.getToggleSortingHandler()}
                >
                  อายุ (วัน){sortArrow(agingCol)}
                </button>
              </th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={11} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่พบใบแจ้งหนี้
                </td>
              </tr>
            )}
            {pageRows.map(({ original: r }) => {
              const total = toNumber(r.totalAmount);
              const paid = toNumber(r.paidAmount);
              // outstanding = total − paid − credited (Phase 2b; credited reads 0 today).
              const outstanding = total - paid - toNumber(r.creditedAmount);
              const isOverdue = r.status === 'POSTED' && new Date(r.dueDate).getTime() < nowMs;
              const tone = TONE_CLASS[criticality(r, nowMs)];
              return (
                <tr key={r.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => onToggle(r.id)}
                    />
                  </td>
                  <td className="code">
                    <a
                      style={{ color: 'var(--brand)', fontWeight: 500, cursor: 'pointer' }}
                      onClick={() => onOpen(r.id)}
                    >
                      {r.voucherNo}
                    </a>
                  </td>
                  <td>{r.customerName}</td>
                  <td className="muted">{r.branchName ?? '—'}</td>
                  <td>{fmtDate(r.invoiceDate)}</td>
                  <td className={tone}>{fmtDate(r.dueDate)}</td>
                  <td className="num tnum">{fmt(total)}</td>
                  <td className="num tnum">{paid > 0 ? fmt(paid) : '—'}</td>
                  <td className="num tnum">
                    <b>{fmt(outstanding)}</b>
                  </td>
                  <td className={tone ? `num ${tone}` : 'num'}>
                    {r.agingDays > 0 ? (
                      <span className="neg" style={{ fontWeight: 600 }}>
                        +{r.agingDays}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {r.status === 'PAID' ? (
                      <span className="badge positive">
                        <span className="bdot" />
                        ชำระแล้ว
                      </span>
                    ) : r.status === 'VOID' ? (
                      <span className="badge neutral">
                        <span className="bdot" />
                        ยกเลิก
                      </span>
                    ) : isOverdue ? (
                      <span className="badge negative">
                        <span className="bdot" />
                        เกินกำหนด
                      </span>
                    ) : (
                      <span className="badge info">
                        <span className="bdot" />
                        รอชำระ
                      </span>
                    )}{' '}
                    <RowActions
                      canReceive={r.status !== 'PAID' && r.status !== 'VOID'}
                      // Voiding is reserved for a posted, untouched invoice (no
                      // payments, no credit notes ⇒ outstanding === total).
                      canVoid={
                        r.status === 'POSTED' && paid === 0 && toNumber(r.creditedAmount) === 0
                      }
                      onOpen={() => onOpen(r.id)}
                      onReceive={() => onReceive(r)}
                      {...(onVoid ? { onVoid: () => onVoid(r) } : {})}
                    />
                  </td>
                </tr>
              );
            })}
            {totalRows > 0 && (
              <tr className="total">
                <td colSpan={6}>รวม {totalRows} ใบ</td>
                <td className="num tnum">
                  {fmt(rows.reduce((a, r) => a + toNumber(r.totalAmount), 0))}
                </td>
                <td className="num tnum">
                  {fmt(rows.reduce((a, r) => a + toNumber(r.paidAmount), 0))}
                </td>
                <td className="num tnum">
                  {fmt(
                    rows.reduce(
                      (a, r) =>
                        a +
                        (toNumber(r.totalAmount) -
                          toNumber(r.paidAmount) -
                          toNumber(r.creditedAmount)),
                      0,
                    ),
                  )}
                </td>
                <td colSpan={2} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="tbl-footer">
        <span>
          {totalRows === 0
            ? 'แสดง 0 รายการ'
            : `แสดง ${firstShown}–${lastShown} จาก ${totalRows} รายการ`}
        </span>
        {table.getPageCount() > 1 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button
              className="btn ghost"
              style={{ padding: '2px 8px' }}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="หน้าก่อนหน้า"
            >
              <Icon name="chevron" size={14} style={{ transform: 'scaleX(-1)' }} />
            </button>
            <span className="muted">
              หน้า {pageIndex + 1} / {table.getPageCount()}
            </span>
            <button
              className="btn ghost"
              style={{ padding: '2px 8px' }}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="หน้าถัดไป"
            >
              <Icon name="chevron" size={14} />
            </button>
          </span>
        )}
      </div>
    </>
  );
}
